import {
  CheckBoxFilled,
  CheckBoxOutlineBlank,
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@project-lary/react-material-symbols';
import { useState } from 'react';
import { cn } from '../utils/tailwind-merge';
import { Badge } from './badge';
import { Button, ButtonSize, ButtonVariant } from './button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from './command';

export interface MultiSelectItem {
  label: string;
  value: string;
  immutable?: boolean;
}

export interface MultiSelectProps {
  items: MultiSelectItem[];
  selectedItems: MultiSelectItem[];
  placeholder?: string;
  onChange: (newSelectedItems: MultiSelectItem[]) => void;
  showSelectedItems?: boolean;
}

function MultiSelect({ items, selectedItems, placeholder, onChange, showSelectedItems = true }: MultiSelectProps) {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  const filteredItems = items.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase()));

  const addSelectedItem = (newItem: MultiSelectItem) => {
    onChange([...selectedItems, newItem]);
  };

  const removeSelectedItem = (oldItem: MultiSelectItem) => {
    onChange(selectedItems.filter((item) => item.value !== oldItem.value));
  };

  return (
    <Command className="w-full overflow-visible">
      {showSelectedItems ? (
        <div className="flex flex-col rounded-md border border-neutral-alpha-5 bg-white px-2">
          {selectedItems.length ? (
            <div className="inline-flex flex-wrap items-center gap-2 pt-2">
              {selectedItems.map((selectedItem, index) => {
                return (
                  <Badge key={`selected-item-${index}`} className="rounded-md">
                    <div className="flex items-center gap-1">
                      <div>{selectedItem.label}</div>
                      {!selectedItem.immutable && (
                        <button
                          className="cursor-pointer"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              removeSelectedItem(selectedItem);
                            }
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeSelectedItem(selectedItem);
                          }}
                        >
                          <div className="flex items-center text-xl">
                            <Close className="text-xl" />
                          </div>
                        </button>
                      )}
                    </div>
                  </Badge>
                );
              })}
            </div>
          ) : null}

          <CommandInput
            placeholder={placeholder}
            value={inputValue}
            onValueChange={setInputValue}
            onBlur={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            className="border-0 outline-none"
          />
        </div>
      ) : (
        <div className="flex">
          <Button
            size={ButtonSize.l}
            variant={ButtonVariant.surface_neutral}
            Icon={open ? KeyboardArrowUp : KeyboardArrowDown}
            onBlur={() => setOpen(false)}
            onClick={() => setOpen(!open)}
            className="flex justify-center"
            iconPosition="right"
          >
            <div className="flex w-full items-center">
              <div className="py-1 text-sm">{placeholder}</div>
            </div>
          </Button>
        </div>
      )}

      <div className="relative">
        {open && (
          <CommandList>
            <CommandEmpty>Empty</CommandEmpty>

            {filteredItems.map((item) => {
              const isSelected = selectedItems.some((selectedItem) => selectedItem.value === item.value);

              return (
                <CommandItem
                  value={item.label}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onSelect={() => {
                    setInputValue('');
                    if (isSelected) {
                      removeSelectedItem(item);
                    } else {
                      addSelectedItem(item);
                    }
                  }}
                  key={item.value}
                  className={cn('my-1 cursor-pointer', {
                    'bg-accent-alpha-4 aria-selected:bg-accent-alpha-3': isSelected,
                  })}
                >
                  <div className="flex items-center gap-1">
                    {isSelected ? (
                      <CheckBoxFilled className="text-accent-9" />
                    ) : (
                      <CheckBoxOutlineBlank className="text-neutral-alpha-7" />
                    )}
                    <div>{item.label}</div>
                  </div>
                </CommandItem>
              );
            })}
          </CommandList>
        )}
      </div>
    </Command>
  );
}

export default MultiSelect;
